import type { ChangeEvent, ComponentType, DragEvent, SVGProps } from 'react';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useForm, useStore } from '@tanstack/react-form';
import {
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { ArrowRight } from '@untitledui/icons/ArrowRight';
import { Clipboard } from '@untitledui/icons/Clipboard';
import { Globe01 } from '@untitledui/icons/Globe01';
import { Upload01 } from '@untitledui/icons/Upload01';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProjectCreateNavState } from '@/routes/researchers/_authenticated/projects/-project-create-nav-state';

import { KindOfResearch } from '@/api/enums';
import { Button, LinkButton } from '@/components/base/buttons/button';
import { AutoResizeTextarea } from '@/components/base/textarea/auto-resize-textarea';
import { BASE_PATH } from '@/constants/config';
import { organizationQueryOptions } from '@/hooks/api/use-organization';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { useTheme } from '@/providers/theme-provider';
import { cx } from '@/utils/cx';
import { getStudyLanguageLabel } from '@common/utils';

import { DELIVERY_MODE_LABELS } from './$projectId/-ai/-project-type-preview';
import {
  getStudyTypeLabel,
  getStudyTypeVisual,
} from './$projectId/-study-type-config';
import {
  DEFAULT_STUDY_LANGUAGE,
  DELIVERY_MODE_ICONS,
} from './-components/ai-interview-settings';
import { AnimatedPlaceholder } from './-components/animated-placeholder';
import { NewProjectSetupModal } from './-components/new-project-setup-modal';
import { resolveProjectCreationDestination } from './-components/use-start-project-creation';
import { extractBriefText } from './-extract-brief-text';

export const Route = createFileRoute(
  '/researchers/_authenticated/projects/prompt',
)({
  beforeLoad: async ({ location, context }) => {
    const setupState = location.state as ProjectCreateNavState | null;
    if (!hasSetupState(setupState)) {
      throw redirect({ to: '/researchers/projects', replace: true });
    }

    // The prompt page IS an AI entry point (free-text prompt, brief upload),
    // so it is only served while the organization may use AI. The same
    // decision the setup modal takes (`resolveProjectCreationDestination`)
    // is applied here for direct navigation: opted out, a failed organization
    // read, or no organization all fail closed into the manual editor with
    // the selected study configuration intact.
    // `fetchQuery` honours the 5-minute staleTime (`ensureQueryData` would
    // serve a stale org forever); `select` does not run here, hence the
    // inline check.
    const organizationId = context.activeDashboard.organization;
    const organization = organizationId
      ? await context.queryClient
          .fetchQuery(organizationQueryOptions(organizationId, context.user.id))
          .catch(() => null)
      : null;
    const canUseAi =
      !!organization && organization.features?.['ai-opted-out'] !== true;
    const destination = resolveProjectCreationDestination(setupState, canUseAi);
    if (destination.to !== '/researchers/projects/prompt') {
      throw redirect({
        to: destination.to,
        params: destination.params,
        replace: true,
        state: destination.state as Record<string, unknown>,
      });
    }
  },
  component: ProjectPromptPage,
});

const examplePrompts = [
  () =>
    t`I work at a fintech startup and we want to understand how small business owners manage their invoicing and cash flow...`,
  () =>
    t`We're a healthcare company exploring how patients over 50 navigate telehealth platforms and what frustrations they encounter...`,
  () =>
    t`Our e-commerce team wants to learn why shoppers abandon carts during checkout and what would bring them back...`,
  () =>
    t`I'm a product manager at an ed-tech company and we need to understand how teachers evaluate and adopt new classroom tools...`,
  () =>
    t`We're redesigning our mobile banking app and want to observe how Gen Z users handle budgeting and savings goals...`,
];

function hasSetupState(
  state: ProjectCreateNavState | null | undefined,
): state is ProjectCreateNavState {
  return !!state?.platform;
}

// `.docx` is intentionally absent — we don't parse Word docs (see -extract-brief-text).
const BRIEF_ACCEPT =
  '.txt,.md,.markdown,.pdf,text/plain,text/markdown,application/pdf';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface SelectedSetupItem {
  key: string;
  label: string;
  value: string;
  icon: IconComponent;
  iconColor?: string;
}

function getSelectedSetupItems(
  setupState: ProjectCreateNavState,
): SelectedSetupItem[] {
  const items: SelectedSetupItem[] = [];

  if (
    setupState.kindOfResearch != null &&
    setupState.researchConductType != null
  ) {
    const { icon, iconColor } = getStudyTypeVisual(
      setupState.kindOfResearch,
      setupState.researchConductType,
    );
    items.push({
      key: 'project-type',
      label: t`Project type`,
      value: getStudyTypeLabel(
        setupState.kindOfResearch,
        setupState.researchConductType,
      ),
      icon,
      iconColor,
    });
  }

  if (
    setupState.kindOfResearch === KindOfResearch.AI_MODERATED_INTERVIEW &&
    setupState.interviewDeliveryMode != null
  ) {
    items.push({
      key: 'interview-format',
      label: t`Format`,
      value: DELIVERY_MODE_LABELS[setupState.interviewDeliveryMode](),
      icon: DELIVERY_MODE_ICONS[setupState.interviewDeliveryMode],
    });
  }

  if (setupState.kindOfResearch === KindOfResearch.AI_MODERATED_INTERVIEW) {
    items.push({
      key: 'study-language',
      label: t`Interview language`,
      value: getStudyLanguageLabel(
        setupState.studyLanguage ?? DEFAULT_STUDY_LANGUAGE,
      ),
      icon: Globe01,
    });
  }

  return items;
}

function SelectedSetupSummary({
  setupState,
  onEdit,
}: {
  setupState: ProjectCreateNavState;
  onEdit: () => void;
}) {
  const items = getSelectedSetupItems(setupState);
  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {items.map(({ key, value, icon: Icon }) => (
        <Button
          color="tertiary"
          size="xs"
          key={key}
          onClick={onEdit}
          iconLeading={<Icon className="size-4 shrink-0" />}
        >
          {value}
        </Button>
      ))}
    </div>
  );
}

function ProjectPromptPage() {
  const { activeDashboard } = Route.useRouteContext();
  const navigate = useNavigate();
  const location = useLocation();
  const analytics = useAnalytics();
  const setupState = location.state as unknown as ProjectCreateNavState;

  useEffect(() => {
    analytics?.screen({ name: 'aiPrompt' });
    // Funnel start anchor. The prompt page is only reachable via the
    // new-project funnel (beforeLoad redirects otherwise), so the entry
    // source is always `new_project`.
    analytics?.track({
      action: 'started',
      actionSubject: 'projectCreation',
      attributes: { entry_source: 'new_project' },
    });
  }, [analytics]);

  const { resolvedTheme = 'light' } = useTheme();
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [isPromptFocused, setIsPromptFocused] = useState(false);

  const form = useForm({ defaultValues: { prompt: '' } });
  const prompt = useStore(form.store, (s) => s.values.prompt);

  const hasPrompt = prompt.trim().length > 0;
  const navigateToEditor = useCallback(
    (navState: ProjectCreateNavState) => {
      void navigate({
        to: '/researchers/projects/$projectId/edit',
        replace: true,
        params: { projectId: 'new' },
        state: navState as Record<string, unknown>,
      });
    },
    [navigate],
  );

  const handleSubmit = useCallback(() => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) return;

    const navState: ProjectCreateNavState = {
      ...setupState,
      prompt: trimmedPrompt,
    };

    // Kicks off AI generation. Never log the prompt text — only its length
    // and the input method.
    analytics?.track({
      action: 'submitted',
      actionSubject: 'creationPrompt',
      attributes: {
        input_method: 'free_text',
        template_id: null,
        prompt_length: trimmedPrompt.length,
        platform: setupState.platform,
      },
    });

    navigateToEditor(navState);
  }, [analytics, navigateToEditor, prompt, setupState]);

  const handleStartFromScratch = useCallback(() => {
    const navState: ProjectCreateNavState = {
      ...setupState,
      prompt: undefined,
    };

    // Measures AI-assisted vs manual preference — a key adoption signal.
    analytics?.track({
      action: 'startedFromScratch',
      actionSubject: 'projectCreation',
      attributes: { platform: setupState.platform },
    });

    navigateToEditor(navState);
  }, [analytics, navigateToEditor, setupState]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isBriefDragTarget, setIsBriefDragTarget] = useState(false);

  const handleBriefFile = useCallback(
    async (file: File) => {
      setUploadError(null);
      setIsUploading(true);
      try {
        const result = await extractBriefText(file);
        if (!result.ok) {
          setUploadError(
            result.reason === 'unsupported'
              ? t`Unsupported file. Upload a .txt, .md, or .pdf file.`
              : result.reason === 'too-large'
                ? t`That file is too large. Upload a smaller file.`
                : result.reason === 'empty'
                  ? t`We couldn't find any text in that file. If it's a scanned PDF, paste your brief instead.`
                  : t`Couldn't read that file. Please try again.`,
          );
          return;
        }

        // Never log the brief content — only the input method and length.
        analytics?.track({
          action: 'submitted',
          actionSubject: 'creationPrompt',
          attributes: {
            input_method: 'document_upload',
            template_id: null,
            prompt_length: result.text.length,
            platform: setupState.platform,
          },
        });

        navigateToEditor({
          ...setupState,
          prompt: result.text,
          briefFileName: file.name,
        });
      } finally {
        setIsUploading(false);
      }
    },
    [analytics, navigateToEditor, setupState],
  );

  const handleBriefFileSelected = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset so picking the same file again still fires onChange.
      event.target.value = '';
      if (file) void handleBriefFile(file);
    },
    [handleBriefFile],
  );

  const handleBriefDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (event.dataTransfer.types.includes('Files') && !isUploading) {
        setIsBriefDragTarget(true);
      }
    },
    [isUploading],
  );

  const handleBriefDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = isUploading ? 'none' : 'copy';
    },
    [isUploading],
  );

  const handleBriefDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (
        event.relatedTarget instanceof Node &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }
      setIsBriefDragTarget(false);
    },
    [],
  );

  const handleBriefDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      event.preventDefault();
      setIsBriefDragTarget(false);
      const file = event.dataTransfer.files[0];
      if (file && !isUploading) void handleBriefFile(file);
    },
    [handleBriefFile, isUploading],
  );

  const column = 'mx-auto w-full max-w-3xl px-4';

  return (
    <div className="flex flex-1 flex-col">
      <NewProjectSetupModal
        isOpen={isSetupModalOpen}
        onOpenChange={setIsSetupModalOpen}
        organizationId={activeDashboard.organization}
        currentSelection={setupState}
      />

      <div className={cx(column, 'flex flex-col gap-8 pt-24 pb-16')}>
        <div className="relative flex flex-col gap-2">
          <LinkButton
            color="link-gray"
            size="xs"
            to="/researchers/projects"
            className="fixed top-8 left-10"
          >
            <img
              src={`${BASE_PATH}/logo-icon-${resolvedTheme}.svg`}
              alt="Respondent logo"
              className="h-5"
            />
          </LinkButton>

          <p className="text-xs font-semibold tracking-wide text-brand-secondary uppercase">
            <Trans>New project</Trans>
          </p>
          <h1 className="font-heading text-display-sm font-semibold text-primary">
            <Trans>Tell us about your project</Trans>
          </h1>
          <p className="max-w-prose text-sm text-tertiary">
            <Trans>
              Describe your project and we&apos;ll set it up for you.
            </Trans>
          </p>
        </div>

        <div className="-mx-4 mb-4 flex flex-col gap-3">
          <div
            className={cx(
              'group relative rounded-2xl border border-gray-300 bg-primary shadow-3xl transition-all duration-200 focus-within:border-transparent focus-within:ring-2 focus-within:ring-brand-50 focus-within:ring-offset-2',
              isBriefDragTarget &&
                'border-transparent ring-2 ring-brand ring-offset-2',
            )}
            onFocus={() => setIsPromptFocused(true)}
            onBlur={() => setIsPromptFocused(false)}
            onDragEnter={handleBriefDragEnter}
            onDragOver={handleBriefDragOver}
            onDragLeave={handleBriefDragLeave}
            onDrop={handleBriefDrop}
          >
            {isBriefDragTarget && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-primary/90 text-sm font-semibold text-brand-secondary">
                <Trans>Drop your brief here</Trans>
              </div>
            )}
            {!hasPrompt && (
              <AnimatedPlaceholder
                phrases={examplePrompts.map((p) => p())}
                active={!isPromptFocused}
                className="pointer-events-none absolute top-4 right-4 left-4 text-md text-placeholder will-change-[transform,opacity,filter]"
              />
            )}
            <AutoResizeTextarea
              value={prompt}
              onChange={(value) => form.setFieldValue('prompt', value)}
              aria-label={t`Describe your research`}
              className="min-h-32 w-full resize-none rounded-2xl bg-transparent p-4 pb-4 text-md text-primary outline-none"
            />
            <div className="flex items-center justify-between gap-3 p-2">
              <SelectedSetupSummary
                setupState={setupState}
                onEdit={() => setIsSetupModalOpen(true)}
              />

              <div className="ml-auto flex flex-row items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={BRIEF_ACCEPT}
                  className="hidden"
                  disabled={isUploading}
                  onChange={handleBriefFileSelected}
                  data-testid="researcher-prompt-brief-upload-input"
                />
                <Button
                  color="tertiary"
                  size="sm"
                  iconLeading={Upload01}
                  className="rounded-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  data-testid="researcher-prompt-upload-brief-button"
                >
                  <Trans>Upload a brief</Trans>
                </Button>

                <Button
                  onClick={handleSubmit}
                  iconLeading={ArrowRight}
                  className="rounded-full"
                  size="sm"
                  aria-label={t`Continue to project setup`}
                  disabled={!hasPrompt}
                />
              </div>
            </div>
          </div>

          {/* Manual path — outside the prompt card so the card stays focused
              on the brief itself. */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              color="tertiary"
              size="sm"
              iconLeading={() => <Clipboard size={18} />}
              // Slides in on hover: width + the size-sm gap (gap-1 → -ml-1)
              // collapse when idle so the button keeps its resting width.
              iconTrailing={
                <ArrowRight
                  aria-hidden="true"
                  className="pointer-events-none -ml-1 h-4 w-0 opacity-0 transition-all duration-200 group-hover:ml-0 group-hover:w-4 group-hover:opacity-100"
                />
              }
              className="rounded-full"
              onClick={handleStartFromScratch}
              data-testid="researcher-prompt-start-from-scratch-button"
            >
              <Trans>Use a standard form instead</Trans>
            </Button>
          </div>
        </div>

        {uploadError && (
          <p className="-mt-2 text-sm text-error-primary" role="alert">
            {uploadError}
          </p>
        )}

        <div className="grid gap-4">
          <p className="mx-2 text-xs tracking-widest text-secondary uppercase">
            <Trans>A strong brief covers</Trans>
          </p>
          <ul className="flex flex-wrap gap-3">
            {BRIEF_INGREDIENTS.map((ingredient) => (
              <li
                key={ingredient()}
                className="rounded-full border border-gray-200 bg-primary px-2.5 py-1 text-xs font-medium text-secondary"
              >
                {ingredient()}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const BRIEF_INGREDIENTS: Array<() => string> = [
  () => t`Research goal`,
  () => t`Who you want to talk to`,
  () => t`Must-haves & deal-breakers`,
  () => t`Timeline & sessions`,
];
